import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { FileText, FileSpreadsheet, Download, Trash2 } from 'lucide-react';
import { useTestRuns } from '../../../hooks/control-testing/useTestRuns.ts';
import { useDeleteControlTestRun } from '../../../hooks/control-testing/useDeleteControlTestRun.ts';
import { buildListParams, DEFAULT_SORT, SORT_DEFAULT_DIR } from '../utils/buildListParams.ts';
import { blobErrorMessage, downloadWorkingPaper, type WorkingPaperFormat } from '../report/download.ts';
import { Filters } from '../components/Filters.tsx';
import { Table } from '../components/table/index.ts';
import { TablePaginationFooter } from '../../../shared/table/TablePaginationFooter.tsx';
import { DEFAULT_PAGE_SIZE, normalizePageSize } from '../../../shared/table/pagination.ts';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from '../../../components/ReadOnlyBanner.tsx';
import { MODULE_KEYS } from '../../../constants/moduleAccess.ts';
import ModuleAuditDownloadButton from '../../../components/shared/ModuleAuditDownloadButton.tsx';
import AttributeTestResultModal from '../../../components/test-runs/AttributeTestResultModal.js';
import { resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import { TENANT_PATHS } from '../../../routing/paths.ts';
import { queryKeys } from '../../../queryKeys.ts';
import type {
  OrgRunControlType,
  OrgRunDatePreset,
  OrgRunsSortBy,
  OrgRunsSortState,
  OrgRunStatusGroup,
  OrgRunTestType,
  OrgTestRunRow,
} from '../../../types/controlTestRun.ts';

/** The org-wide Test Runs page: every control's control-testing runs (test_runs), one row per rerun chain. */
export const TestRunsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedAuditYear, selectedAuditYearId } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState<string>('');
  const [datePreset, setDatePreset] = useState<OrgRunDatePreset>('all');
  const [statusFilter, setStatusFilter] = useState<OrgRunStatusGroup>('all');
  const [testTypeFilter, setTestTypeFilter] = useState<OrgRunTestType>('all');
  const [controlTypeFilter, setControlTypeFilter] = useState<OrgRunControlType>('all');
  const [sort, setSort] = useState<OrgRunsSortState>(() => ({ ...DEFAULT_SORT }));
  const [deleteRun, setDeleteRun] = useState<OrgTestRunRow | null>(null);
  const [downloadRun, setDownloadRun] = useState<OrgTestRunRow | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [resultsRunId, setResultsRunId] = useState<string | null>(null);

  // Search and year changes go back to page 1 in the same update as the new key, so no request is
  // made for the old page with the new search or year.
  useEffect(() => {
    const next = searchInput.trim();
    if (next === debouncedSearchKey) return undefined;
    const t = window.setTimeout(() => {
      setDebouncedSearchKey(next);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput, debouncedSearchKey]);

  const [yearSeen, setYearSeen] = useState(selectedAuditYearId);
  if (yearSeen !== selectedAuditYearId) {
    setYearSeen(selectedAuditYearId);
    setPage(1);
  }

  const listParams = useMemo(
    () =>
      buildListParams(
        page,
        pageSize,
        {
          datePreset,
          status: statusFilter,
          testType: testTypeFilter,
          controlType: controlTypeFilter,
          search: debouncedSearchKey || undefined,
          auditYearId: selectedAuditYearId,
        },
        sort
      ),
    [page, pageSize, datePreset, statusFilter, testTypeFilter, controlTypeFilter, sort, debouncedSearchKey, selectedAuditYearId]
  );

  const { data, isLoading, isError, refetch, error } = useTestRuns(listParams);
  const deleteMutation = useDeleteControlTestRun();

  const { rows, total, totalPages, canPrev, canNext } = useMemo(() => {
    const r = data?.rows ?? [];
    const t = data?.total ?? 0;
    const serverTp = data?.totalPagesFromApi;
    const tp = serverTp != null && serverTp >= 1 ? serverTp : Math.max(1, Math.ceil(t / pageSize));
    return { rows: r, total: t, totalPages: tp, canPrev: page > 1, canNext: page < tp };
  }, [data?.rows, data?.total, data?.totalPagesFromApi, page, pageSize]);

  /** If `total_pages` drops (e.g. delete on last page), avoid stale `page` > last page — judged only
   * on real data, never while a page is still loading. */
  const hasData = data != null;
  useEffect(() => {
    if (!hasData) return;
    setPage((p) => (p > totalPages ? totalPages : p));
  }, [hasData, totalPages]);

  const withPageReset = useCallback(
    <V,>(setter: (v: V) => void) =>
      (v: V) => {
        setter(v);
        setPage(1);
      },
    []
  );

  const handleSortChange = useCallback((key: OrgRunsSortBy) => {
    setSort((prev) => (prev.key !== key ? { key, dir: SORT_DEFAULT_DIR[key] } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearchKey('');
    setDatePreset('all');
    setStatusFilter('all');
    setTestTypeFilter('all');
    setControlTypeFilter('all');
    setSort({ ...DEFAULT_SORT });
    setPage(1);
  }, []);

  const openInControl = useCallback(
    (run: OrgTestRunRow) => {
      navigate(`/sox-controls/view/${encodeURIComponent(run.controlId || run.soxControlId)}`, {
        state: { activeTab: 'test-runs', returnPath: TENANT_PATHS.CONTROL_TESTING_TEST_RUNS },
      });
    },
    [navigate]
  );

  /** Deletes every attempt of the rerun chain, as the control's Test Runs tab does. */
  const confirmDelete = useCallback(async () => {
    if (!deleteRun) return;
    try {
      const { remaining } = await deleteMutation.mutateAsync(deleteRun.attemptIds);
      // On a partial failure keep the dialog for a retry of only what is left.
      setDeleteRun(remaining.length === 0 ? null : { ...deleteRun, attemptIds: remaining });
    } catch {
      /* the mutation already showed the error */
    }
  }, [deleteRun, deleteMutation]);

  const handleDownload = useCallback(
    async (format: WorkingPaperFormat) => {
      if (!downloadRun || isDownloading) return;
      setIsDownloading(true);
      try {
        await downloadWorkingPaper(downloadRun.executionId, format);
        setDownloadRun(null);
      } catch (err) {
        toast.error('Download failed: ' + resolve403ErrorMessage(err, await blobErrorMessage(err)));
      } finally {
        setIsDownloading(false);
      }
    },
    [downloadRun, isDownloading]
  );

  const closeResults = useCallback(() => {
    setResultsRunId(null);
    // Sign-off, overrides and exceptions change from the results modal: refresh this page and the control tabs.
    queryClient.invalidateQueries({ queryKey: [...queryKeys.controlTesting.all, 'test-runs'] });
  }, [queryClient]);

  const deleteAttempts = deleteRun?.attemptIds.length ?? 0;

  return (
    <div className="space-y-6">
      <ReadOnlyBanner />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Test Runs</h1>
          <p className="text-sm text-gray-500">All control test runs across controls</p>
        </div>
        <ModuleAuditDownloadButton
          resourceType="test_execution"
          scope="control_test_runs"
          moduleKey={MODULE_KEYS.TEST_EXECUTIONS}
          filenamePrefix="control_test_runs_audit"
        />
      </div>

      <Filters
        searchQuery={searchInput}
        datePreset={datePreset}
        statusFilter={statusFilter}
        testTypeFilter={testTypeFilter}
        controlTypeFilter={controlTypeFilter}
        onSearchChange={setSearchInput}
        onDatePresetChange={withPageReset(setDatePreset)}
        onStatusChange={withPageReset(setStatusFilter)}
        onTestTypeChange={withPageReset(setTestTypeFilter)}
        onControlTypeChange={withPageReset(setControlTypeFilter)}
        onReset={resetFilters}
      />

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3" />
          <p className="text-sm">Loading test runs…</p>
        </div>
      )}

      {isError && !isLoading && (
        <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
          <p className="text-red-600 mb-4">{error?.message || 'Failed to load test runs'}</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <Table
          rows={rows}
          sort={sort}
          onSortChange={handleSortChange}
          onResultsClick={(run) => setResultsRunId(run.executionId)}
          onOpenControlClick={openInControl}
          onDownloadClick={setDownloadRun}
          onDeleteClick={isLocked ? undefined : setDeleteRun}
        />
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No test runs match your filters or search</h3>
          <p className="text-gray-500 mb-6">Test runs are created from a control's Test Runs tab.</p>
          <button
            type="button"
            onClick={resetFilters}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Reset filters
          </button>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <TablePaginationFooter
          className="mt-0 px-0 border-t-0"
          from={(page - 1) * pageSize + 1}
          to={Math.min(page * pageSize, total)}
          total={total}
          itemLabel="test runs"
          pageIndex={page - 1}
          pageCount={totalPages}
          canPreviousPage={canPrev}
          canNextPage={canNext}
          onPreviousPage={() => setPage((p) => Math.max(1, p - 1))}
          onNextPage={() => setPage((p) => p + 1)}
          showPageSizeSelector
          pageSize={pageSize}
          onPageSizeChange={(nextSize) => {
            setPageSize(normalizePageSize(nextSize));
            setPage(1);
          }}
        />
      )}

      {resultsRunId && <AttributeTestResultModal runId={resultsRunId} onClose={closeResults} />}

      {deleteRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Test Run</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Delete {deleteRun.test_run_name || `RUN-${deleteRun.test_run_number}`} on {deleteRun.controlId}
              {deleteAttempts > 1 ? ` and all ${deleteAttempts} of its attempts` : ''}? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteRun(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={() => void confirmDelete()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-60"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-full">
                <Download className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Download Working Paper</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {downloadRun.controlId} · {downloadRun.test_run_name || `RUN-${downloadRun.test_run_number}`}
            </p>
            <div className="space-y-2 mb-6">
              {(
                [
                  { format: 'excel', label: 'Excel Working Paper (.xlsx)', Icon: FileSpreadsheet, color: 'text-green-600' },
                  { format: 'pdf', label: 'PDF Working Paper (.pdf)', Icon: FileText, color: 'text-red-600' },
                ] as const
              ).map(({ format, label, Icon, color }) => (
                <button
                  key={format}
                  type="button"
                  onClick={() => void handleDownload(format)}
                  disabled={isDownloading}
                  className={`w-full px-4 py-3 text-left text-sm font-medium border border-gray-200 rounded-lg flex items-center gap-3 ${
                    isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${color}`} />
                  <span className="flex-1 min-w-0">{label}</span>
                  {isDownloading && <span className="text-xs text-gray-400 flex-shrink-0">Generating...</span>}
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setDownloadRun(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
